/**
 * Payment Slips Hook
 * Custom hook for managing payment slip data with React Query
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import type {
  PaginatedSlipsResponse,
  SlipDetailResponse,
  SlipStatisticsResponse,
  ApproveSlipRequest,
  RejectSlipRequest,
  SlipFilters,
  SlipSortOptions,
} from '../types/payment-slip';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

// Axios instance with auth header
const api = axios.create({
  baseURL: `${API_BASE_URL}/api/payment`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('authToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Query keys
export const paymentSlipKeys = {
  all: ['payment-slips'] as const,
  lists: () => [...paymentSlipKeys.all, 'list'] as const,
  list: (filters: SlipFilters & SlipSortOptions & { limit: number; offset: number }) =>
    [...paymentSlipKeys.lists(), filters] as const,
  details: () => [...paymentSlipKeys.all, 'detail'] as const,
  detail: (id: string) => [...paymentSlipKeys.details(), id] as const,
  statistics: (filters?: { startDate?: string; endDate?: string }) =>
    [...paymentSlipKeys.all, 'statistics', filters] as const,
};

/**
 * Fetch pending payment slips
 */
export function usePendingSlips(
  options: {
    limit?: number;
    offset?: number;
    sortBy?: 'submitted_at' | 'stated_amount' | 'reference_number';
    sortOrder?: 'ASC' | 'DESC';
    status?: string;
  } = {}
) {
  const { limit = 50, offset = 0, sortBy = 'submitted_at', sortOrder = 'DESC', status } = options;

  return useQuery({
    queryKey: [...paymentSlipKeys.lists(), { ...options, limit, offset, sortBy, sortOrder }] as const,
    queryFn: async () => {
      const params: Record<string, unknown> = { limit, offset, sortBy, sortOrder };
      if (status) {
        params.status = status;
      }
      const { data } = await api.get<PaginatedSlipsResponse>('/admin/slips/pending', {
        params,
      });
      return data.data;
    },
    staleTime: 30000, // 30 seconds
    refetchInterval: 60000, // Refetch every minute
  });
}

/**
 * Fetch slip details by ID
 */
export function useSlipDetail(slipId: string | null) {
  return useQuery({
    queryKey: paymentSlipKeys.detail(slipId || ''),
    queryFn: async () => {
      if (!slipId) throw new Error('Slip ID is required');
      const { data } = await api.get<SlipDetailResponse>(`/admin/slips/${slipId}`);
      return data.data;
    },
    enabled: !!slipId,
    staleTime: 60000,
  });
}

/**
 * Fetch slip statistics
 */
export function useSlipStatistics(filters?: { startDate?: string; endDate?: string }) {
  return useQuery({
    queryKey: paymentSlipKeys.statistics(filters),
    queryFn: async () => {
      const { data } = await api.get<SlipStatisticsResponse>('/admin/slips/statistics', {
        params: filters,
      });
      return data.data;
    },
    staleTime: 60000,
    refetchInterval: 120000, // Refetch every 2 minutes
  });
}

/**
 * Mark slip as under review
 */
export function useMarkSlipUnderReview() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (slipId: string) => {
      const { data } = await api.post(`/admin/slips/${slipId}/review`);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: paymentSlipKeys.lists() });
      queryClient.invalidateQueries({ queryKey: paymentSlipKeys.statistics() });
    },
  });
}

/**
 * Approve payment slip
 */
export function useApproveSlip() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      slipId,
      request,
    }: {
      slipId: string;
      request: ApproveSlipRequest;
    }) => {
      const { data } = await api.post(`/admin/slips/${slipId}/approve`, request);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: paymentSlipKeys.lists() });
      queryClient.invalidateQueries({ queryKey: paymentSlipKeys.statistics() });
    },
  });
}

/**
 * Reject payment slip
 */
export function useRejectSlip() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      slipId,
      request,
    }: {
      slipId: string;
      request: RejectSlipRequest;
    }) => {
      const { data } = await api.post(`/admin/slips/${slipId}/reject`, request);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: paymentSlipKeys.lists() });
      queryClient.invalidateQueries({ queryKey: paymentSlipKeys.statistics() });
    },
  });
}
